import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { API_BASE } from "config/api";

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const NameCell = ({ value }) => (
  <Typography variant="button" fontWeight={600}>
    {value}
  </Typography>
);
NameCell.propTypes = { value: PropTypes.string.isRequired };

const ActionsCell = ({ row, handleViewQr }) => (
  <Button
    size="small"
    variant="outlined"
    color="info"
    startIcon={<QrCode2Icon fontSize="small" />}
    onClick={() => handleViewQr(row.original)}
  >
    View QR Code
  </Button>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleViewQr: PropTypes.func.isRequired,
};

export default function useQrCodeData() {
  const { church } = useAuth();

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrMember, setQrMember] = useState(null);
  const [qrToken, setQrToken] = useState(null);
  const [isLoadingQr, setIsLoadingQr] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem("authToken");
    return token ? { Accept: "application/json", Authorization: `Bearer ${token}` } : null;
  };

  const fetchMembers = useCallback(async () => {
    if (!church?.id) return;
    setIsLoading(true);
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/members/church/${church.id}`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const members = Array.isArray(result) ? result : result.data || [];
      setRows(
        members.map((m, index) => ({
          id: m.id,
          serial: index + 1,
          name: `${m.first_name} ${m.surname}`,
          memberId: m.memberID || "—",
          phone: m.phone || "—",
          originalData: m,
        }))
      );
      setError(null);
    } catch (e) {
      console.error("Fetch members failed:", e);
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [church?.id]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleViewQr = useCallback(async (member) => {
    setQrMember(member.originalData);
    setQrToken(null);
    setQrModalOpen(true);
    setIsLoadingQr(true);
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/members/${member.id}/qr-code`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setQrToken(result.data?.qr_token || null);
    } catch (e) {
      console.error("Fetch QR code failed:", e);
    } finally {
      setIsLoadingQr(false);
    }
  }, []);

  const handlePrint = () => window.print();

  const columns = [
    { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
    { Header: "Name", accessor: "name", align: "left", width: "30%", Cell: NameCell },
    { Header: "Member ID", accessor: "memberId", align: "left", width: "20%" },
    { Header: "Phone", accessor: "phone", align: "left", width: "20%" },
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      width: "25%",
      Cell: (props) => <ActionsCell {...props} handleViewQr={handleViewQr} />,
    },
  ];

  return {
    columns,
    rows,
    isLoading,
    error,
    qrModalOpen,
    setQrModalOpen,
    qrMember,
    qrToken,
    isLoadingQr,
    handlePrint,
  };
}
